import { useState, useEffect } from 'react';
import { useConfirmation } from '../../components/ConfirmationModal';

const DataSettings = ({ settings, onSave, onChange }) => {
  const { showConfirmation, ConfirmationComponent } = useConfirmation();
  const [formData, setFormData] = useState({
    autoArchiveInvoices: true,
    archiveAfterDays: 365,
    autoDeleteDrafts: false,
    deleteDraftsAfterDays: 30,
    dataRetentionPeriod: 2555, // 7 years
    exportFormat: 'json',
    includeDocuments: true,
    includeImages: false,
    compressionLevel: 'medium',
    ...settings
  });

  const [exportProgress, setExportProgress] = useState(null);
  const [importProgress, setImportProgress] = useState(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        autoArchiveInvoices: settings.autoArchiveInvoices ?? true,
        archiveAfterDays: settings.archiveAfterDays ?? 365,
        autoDeleteDrafts: settings.autoDeleteDrafts ?? false,
        deleteDraftsAfterDays: settings.deleteDraftsAfterDays ?? 30,
        dataRetentionPeriod: settings.dataRetentionPeriod ?? 2555,
        exportFormat: settings.exportFormat || 'json',
        includeDocuments: settings.includeDocuments ?? true,
        includeImages: settings.includeImages ?? false,
        compressionLevel: settings.compressionLevel || 'medium'
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : 
                     type === 'number' ? parseInt(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    if (onChange) onChange();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const exportData = async (type) => {
    setExportProgress({ type, progress: 0 });
    
    // Simulate export progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 200));
      setExportProgress({ type, progress: i });
    }
    
    setExportProgress(null);
    alert(`${type} export completed! In a real app, this would download the file.`);
  };

  const importData = (file) => {
    if (!file) return;
    
    setImportProgress({ progress: 0 });
    
    // Simulate import progress
    const reader = new FileReader();
    reader.onload = async (e) => {
      for (let i = 0; i <= 100; i += 20) {
        await new Promise(resolve => setTimeout(resolve, 300));
        setImportProgress({ progress: i });
      }
      
      setImportProgress(null);
      alert('Import completed! In a real app, this would process the imported data.');
    };
    
    reader.readAsText(file);
  };

  const clearAllData = async () => {
    const firstConfirmed = await showConfirmation({
      title: 'Clear All Data',
      message: 'Are you sure you want to clear ALL data? This action cannot be undone!',
      confirmText: 'Continue',
      variant: 'danger'
    });
    
    if (firstConfirmed) {
      const finalConfirmed = await showConfirmation({
        title: 'Final Confirmation',
        message: 'This will permanently delete all invoices, quotes, clients, and settings. This cannot be undone!',
        confirmText: 'DELETE ALL DATA',
        variant: 'danger'
      });
      
      if (finalConfirmed) {
        // In a real app, this would call the API to clear data
        alert('Data clearing functionality will be implemented with backend integration');
      }
    }
  };

  const archiveOldData = async () => {
    const confirmed = await showConfirmation({
      title: 'Archive Data',
      message: `Archive data older than ${formData.archiveAfterDays} days?`,
      confirmText: 'Archive Data',
      variant: 'warning'
    });
    
    if (confirmed) {
      alert('Data archiving functionality will be implemented with backend integration');
    }
  };

  const calculateStorageUsage = () => {
    // In a real app, this would calculate actual storage usage
    return {
      invoices: 2.5,
      quotes: 1.2,
      clients: 0.3,
      documents: 15.8,
      images: 8.4,
      total: 28.2
    };
  };

  const storageUsage = calculateStorageUsage();

  return (
    <>
      <ConfirmationComponent />
      <div className="card">
      <div className="card-header">
        <h6 className="mb-0">
          <i className="fas fa-database me-2"></i>
          Data Management
        </h6>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Storage Usage */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Storage Usage</h6>
            </div>
            
            <div className="col-md-6">
              <div className="card bg-light">
                <div className="card-body">
                  <h5 className="card-title">Total Storage Used</h5>
                  <h3 className="text-primary">{storageUsage.total} MB</h3>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between small">
                      <span>Invoices</span>
                      <span>{storageUsage.invoices} MB</span>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-primary" 
                        style={{ width: `${(storageUsage.invoices / storageUsage.total) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="d-flex justify-content-between small">
                      <span>Documents</span>
                      <span>{storageUsage.documents} MB</span>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-success" 
                        style={{ width: `${(storageUsage.documents / storageUsage.total) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="d-flex justify-content-between small">
                      <span>Images</span>
                      <span>{storageUsage.images} MB</span>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-warning" 
                        style={{ width: `${(storageUsage.images / storageUsage.total) * 100}%` }}
                      ></div>
                    </div>
                    
                    <div className="d-flex justify-content-between small">
                      <span>Other</span>
                      <span>{(storageUsage.quotes + storageUsage.clients).toFixed(1)} MB</span>
                    </div>
                    <div className="progress" style={{ height: '6px' }}>
                      <div 
                        className="progress-bar bg-secondary" 
                        style={{ width: `${((storageUsage.quotes + storageUsage.clients) / storageUsage.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="row">
                <div className="col-6 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <i className="fas fa-file-invoice fa-2x text-primary mb-2"></i>
                      <div className="h6">245</div>
                      <small className="text-muted">Total Invoices</small>
                    </div>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <i className="fas fa-file-alt fa-2x text-info mb-2"></i>
                      <div className="h6">89</div>
                      <small className="text-muted">Total Quotes</small>
                    </div>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <i className="fas fa-users fa-2x text-success mb-2"></i>
                      <div className="h6">56</div>
                      <small className="text-muted">Total Clients</small>
                    </div>
                  </div>
                </div>
                <div className="col-6 mb-3">
                  <div className="card text-center">
                    <div className="card-body">
                      <i className="fas fa-file fa-2x text-warning mb-2"></i>
                      <div className="h6">128</div>
                      <small className="text-muted">Documents</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Retention */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Data Retention</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="autoArchiveInvoices"
                  checked={formData.autoArchiveInvoices}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Automatically archive old invoices
                </label>
              </div>
            </div>
            
            {formData.autoArchiveInvoices && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Archive invoices after (days)</label>
                <input
                  type="number"
                  className="form-control"
                  name="archiveAfterDays"
                  value={formData.archiveAfterDays}
                  onChange={handleChange}
                  min="30"
                  max="3650"
                />
              </div>
            )}
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="autoDeleteDrafts"
                  checked={formData.autoDeleteDrafts}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Automatically delete old draft invoices/quotes
                </label>
              </div>
            </div>
            
            {formData.autoDeleteDrafts && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Delete drafts after (days)</label>
                <input
                  type="number"
                  className="form-control"
                  name="deleteDraftsAfterDays"
                  value={formData.deleteDraftsAfterDays}
                  onChange={handleChange}
                  min="1"
                  max="365"
                />
              </div>
            )}
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Data retention period (days)</label>
              <select
                className="form-select"
                name="dataRetentionPeriod"
                value={formData.dataRetentionPeriod}
                onChange={handleChange}
              >
                <option value={365}>1 Year</option>
                <option value={1095}>3 Years</option>
                <option value={1825}>5 Years</option>
                <option value={2555}>7 Years (Recommended)</option>
                <option value={3650}>10 Years</option>
                <option value={-1}>Indefinite</option>
              </select>
              <div className="form-text">Legal requirement varies by jurisdiction</div>
            </div>
            
            <div className="col-12">
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={archiveOldData}
              >
                <i className="fas fa-archive me-1"></i>
                Archive Old Data Now
              </button>
            </div>
          </div>

          {/* Export Data */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Export Data</h6>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Export Format</label>
              <select
                className="form-select"
                name="exportFormat"
                value={formData.exportFormat}
                onChange={handleChange}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF Report</option>
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Compression Level</label>
              <select
                className="form-select"
                name="compressionLevel"
                value={formData.compressionLevel}
                onChange={handleChange}
              >
                <option value="none">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Include Options</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="includeDocuments"
                  checked={formData.includeDocuments}
                  onChange={handleChange}
                />
                <label className="form-check-label">Include documents</label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="includeImages"
                  checked={formData.includeImages}
                  onChange={handleChange}
                />
                <label className="form-check-label">Include images</label>
              </div>
            </div>
            
            <div className="col-12 mb-3">
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => exportData('Full Export')}
                  disabled={exportProgress !== null}
                >
                  <i className="fas fa-download me-1"></i>
                  Export All Data
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => exportData('Invoices Only')}
                  disabled={exportProgress !== null}
                >
                  <i className="fas fa-file-invoice me-1"></i>
                  Invoices Only
                </button>
                <button
                  type="button"
                  className="btn btn-outline-info"
                  onClick={() => exportData('Clients Only')}
                  disabled={exportProgress !== null}
                >
                  <i className="fas fa-users me-1"></i>
                  Clients Only
                </button>
              </div>
            </div>
            
            {exportProgress && (
              <div className="col-12 mb-3">
                <div className="alert alert-info">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Exporting {exportProgress.type}...</span>
                    <span>{exportProgress.progress}%</span>
                  </div>
                  <div className="progress mt-2">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${exportProgress.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Import Data */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Import Data</h6>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Import File</label>
              <input
                type="file"
                className="form-control"
                accept=".json,.csv,.xlsx"
                onChange={(e) => importData(e.target.files[0])}
                disabled={importProgress !== null}
              />
              <div className="form-text">
                Supported formats: JSON, CSV, Excel (.xlsx)
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Import Options</label>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" defaultChecked />
                <label className="form-check-label">Validate data before import</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" />
                <label className="form-check-label">Overwrite existing records</label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" defaultChecked />
                <label className="form-check-label">Create backup before import</label>
              </div>
            </div>
            
            {importProgress && (
              <div className="col-12 mb-3">
                <div className="alert alert-success">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Importing data...</span>
                    <span>{importProgress.progress}%</span>
                  </div>
                  <div className="progress mt-2">
                    <div 
                      className="progress-bar bg-success" 
                      style={{ width: `${importProgress.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Database Maintenance */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Database Maintenance</h6>
            </div>
            
            <div className="col-md-6 mb-3">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">
                    <i className="fas fa-broom me-2 text-warning"></i>
                    Clean Up
                  </h6>
                  <p className="card-text small">
                    Remove temporary files, optimize database, and clean up orphaned records.
                  </p>
                  <button className="btn btn-outline-warning btn-sm">
                    <i className="fas fa-broom me-1"></i>
                    Run Cleanup
                  </button>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">
                    <i className="fas fa-tachometer-alt me-2 text-info"></i>
                    Optimize
                  </h6>
                  <p className="card-text small">
                    Optimize database tables and rebuild indexes for better performance.
                  </p>
                  <button className="btn btn-outline-info btn-sm">
                    <i className="fas fa-tachometer-alt me-1"></i>
                    Optimize Database
                  </button>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">
                    <i className="fas fa-chart-line me-2 text-success"></i>
                    Analyze
                  </h6>
                  <p className="card-text small">
                    Generate database statistics and performance reports.
                  </p>
                  <button className="btn btn-outline-success btn-sm">
                    <i className="fas fa-chart-line me-1"></i>
                    Analyze Database
                  </button>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">
                    <i className="fas fa-shield-alt me-2 text-primary"></i>
                    Backup
                  </h6>
                  <p className="card-text small">
                    Create a manual backup of all data and settings.
                  </p>
                  <button className="btn btn-outline-primary btn-sm">
                    <i className="fas fa-shield-alt me-1"></i>
                    Create Backup
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3 text-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Danger Zone
              </h6>
            </div>
            
            <div className="col-12">
              <div className="card border-danger">
                <div className="card-body">
                  <h6 className="card-title text-danger">Clear All Data</h6>
                  <p className="card-text">
                    Permanently delete all invoices, quotes, clients, payments, and settings. 
                    This action cannot be undone.
                  </p>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={clearAllData}
                  >
                    <i className="fas fa-trash me-1"></i>
                    Clear All Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-save me-1"></i>
              Save Data Settings
            </button>
            <button type="button" className="btn btn-outline-secondary">
              <i className="fas fa-download me-1"></i>
              Download System Report
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default DataSettings;